import type { JoinRequest } from "../../../components/UserManagement/JoinRequestList";
import { getSidebarItem } from "../../../config/sidebarConfig";
import { userHomeHref } from "../../../routes";
import type { FamilySearchIndexEntry } from "../../../services/api/family";
import {
  circlesQuery,
  familySearchIndexQuery,
  invitationsQuery,
  joinRequestsQuery,
  usersQuery,
} from "../../../services/queries";
import { viewQuery } from "../scopedQuery";
import { filterActionableJoinRequests } from "../../../utils/joinRequestUtils";
import { parseUserSearchContact } from "../../../utils/userContactUtils";
import { inviteCircles } from "../../circles/circleAccess";
import { familyChildName } from "../../family/familySearch";
import { toUserHome, toUsers, usersHref, type InvitePrefill } from "../../users/usersHandover";
import { askedHow } from "./askedHow";
import { askFor, findByName, findNamedIn, normal } from "./findByName";
import { askForNamedCircle, resolveCircle } from "./pickCircle";
import { pickUser } from "./pickUser";
import { openPageEntry } from "./openPage";
import { AnswerContext, defineEntry, HandoffEntry, isSlotQuestion, SummaryLine } from "./types";
import { capitalize, countOf, isAre, listOf, listUpTo, quoted } from "./wording";

const usersPage = getSidebarItem("users");
const openUsers = (search?: string) => ({
  label: `Open ${usersPage.label}`,
  href: usersHref(),
  ...(search ? { state: { search } } : {}),
});

const users = (n: number) => countOf(n, "User");
const invites = (n: number) => countOf(n, "Invite");
const joinRequests = (n: number) => countOf(n, "Join request");

// The same queries the Users page uses, so each answer is the value it shows
const orgOf = ({ user }: AnswerContext) => user?.schoolId || user?.orgId;

const loadUsers = (ctx: AnswerContext) =>
  ctx.queryClient.fetchQuery(viewQuery(ctx.user, usersQuery(orgOf(ctx), ctx.token)));

/** The Requests tab: Join requests from people who aren't already Users. */
async function loadJoinRequests(ctx: AnswerContext, search = "") {
  const [requests, all] = await Promise.all([
    ctx.queryClient.fetchQuery(viewQuery(ctx.user, joinRequestsQuery(orgOf(ctx), ctx.token))),
    loadUsers(ctx),
  ]);
  return filterActionableJoinRequests(requests, all, search);
}

/** "Lerato Dube (Grade R)", as the Parent and Grade columns show it */
const requestedBy = (request: JoinRequest) => {
  const who = request.parentName?.trim() || `${request.learnerName} ${request.learnerSurname}`.trim();
  return request.gradeClass ? `${who} (${request.gradeClass})` : who;
};

/** The grades Join requests are from, in the order they first appear. */
const gradesOf = (requests: JoinRequest[]) =>
  Array.from(new Set(requests.map((r) => r.gradeClass?.trim()).filter((g): g is string => Boolean(g))));

/** ": a, b and c" after a count of Invites, when there are contacts to name */
const whoTo = (contacts: string[]) => (contacts.length > 0 ? `: ${listUpTo(contacts)}` : "");

/** A child's guardians, as Users search finds them by the child's name. */
type ChildGuardians = { child: string; guardians: string[] };

/** The child a question names, by whole name or first name: "Thandi Mokoena's guardians", "Thandi's parents". */
function pickChild(children: FamilySearchIndexEntry[], said: string) {
  const text = said.replace(/['’]s\b/g, "");
  const names = Array.from(new Set(children.map(familyChildName)));
  const byFullName = findNamedIn(children, text, familyChildName);
  const named = byFullName.length > 0 ? byFullName : findNamedIn(children, text, (c) => c.firstName);
  const found = named.length > 0 ? named : findByName(children, text, familyChildName);
  const foundNames = Array.from(new Set(found.map(familyChildName)));
  if (foundNames.length === 1) return { child: found[0] };
  if (foundNames.length > 1) return askFor("child", "ambiguous", "Which child do you mean?", foundNames);
  // Capitalised words after the first are likely a name we don't know
  const unknown = text.trim().split(/\s+/).slice(1).filter((w) => /^[A-Z]/.test(w) && w !== "I");
  return unknown.length > 0
    ? askFor("child", "notFound", `I couldn't find a child called ${quoted(unknown.join(" ").replace(/[?.!]+$/, ""))}. Which child do you mean?`, names)
    : askFor("child", "missing", "Which child's family would you like to see?", names);
}

/** ": a, b and c" after a count of Users */
const namedAfter = (names: string[]) => (names.length > 0 ? `: ${listUpTo(names)}` : "");

/** The words of "is Jane Dube a user?" or "find user Jane" that aren't the name. */
const FIND_WORDS =
  /\b(?:find|look(?:\s+(?:up|for))?|search(?:\s+for)?|is|are|there|do|does|we|have|has|an?|the|this|that|users?|parents?|person|on|in|app|noticeboard|account|called|named|by|name)\b/gi;
const tidyName = (text: string) => text.replace(/[?.!]+/g, "").replace(/\s+/g, " ").trim();

/** Words for the app, the school or everyone, so an Invite isn't into a Circle. */
const NO_CIRCLE = /^(the )?(app|noticeboard|school|organi[sz]ation|church|everyone)$/;

const CHECK_INVITE =
  "Here's what I understood. Open it to check the details. Nothing is sent until you press Add user.";

/** "Invite sarah@example.com to Grade 2": Users' Invite screen, with the contact and Circle filled in. */
const inviteEntry: HandoffEntry = {
  id: "users.invite",
  sidebarItem: "users",
  examples: [
    "Invite a parent",
    "Invite",
    "Invite someone",
    "Invite a user",
    "Invite a new parent",
    "Invite parents",
    "Invite a family",
    "Add a user",
    "Add a parent",
    "Add a new user",
    "Send an invite",
    "Send an invitation",
    "Get a parent onto the app",
    "What if they have no email, only a phone?",
    ...askedHow("invite a parent instead"),
  ],
  handoff: async (said, ctx) => {
    const invite: InvitePrefill = {};
    const summary: SummaryLine[] = [];
    if (said.contact) {
      const contact = parseUserSearchContact(said.contact) ?? { phoneNumber: said.contact };
      Object.assign(invite, contact);
      summary.push(
        contact.email ? { label: "Email", value: contact.email } : { label: "Phone", value: said.contact }
      );
    }
    if (said.audience && !NO_CIRCLE.test(normal(said.audience))) {
      const all = await ctx.queryClient.fetchQuery(viewQuery(ctx.user, circlesQuery(ctx.user?.schoolId, ctx.token)));
      const circles = inviteCircles(all, ctx.user);
      const question = "Which Circle should they join?";
      const picked = askForNamedCircle(resolveCircle(circles, said.audience), question, circles.map((c) => c.name));
      if (isSlotQuestion(picked)) return picked;
      if (picked.circle) {
        invite.circleIds = [picked.circle.circleId];
        summary.push({ label: "Circle", value: picked.circle.name });
      }
    }
    return {
      kind: "ready",
      text: summary.length > 0 ? CHECK_INVITE : "Open it to add their email or phone number. Nothing is sent until you press Add user.",
      summary,
      to: toUsers({ invite }),
    };
  },
};

/** "Approve the join requests from Grade R": the Requests tab, searched for that grade. Approves nothing. */
const approveJoinRequestsEntry: HandoffEntry = {
  id: "users.approveJoinRequests",
  sidebarItem: "users",
  examples: [
    "Approve the join requests",
    "Approve all the join requests",
    "Approve join requests",
    "Approve a join request",
    "Accept the join requests",
    "Accept all the join requests",
    "Review the join requests",
    "Deal with the join requests",
    "Let the waiting parents in",
    "Approve the new parents",
    "Add the parents who asked to join",
  ],
  handoff: async (said, ctx) => {
    const waiting = await loadJoinRequests(ctx);
    if (waiting.length === 0) {
      return { kind: "cannot", text: "There are no Join requests waiting right now." };
    }
    // "from Grade R" or "for Grade R"; a follow-up's "same for Grade 1" changes it
    const grade = (said.audience ?? said.from)?.trim();
    const found = grade ? filterActionableJoinRequests(waiting, [], grade) : waiting;
    if (grade && found.length === 0) {
      const grades = gradesOf(waiting);
      return askFor(
        said.from ? "from" : "audience",
        "notFound",
        `There are no Join requests from ${quoted(grade)}.${grades.length > 0 ? " Which grade do you mean?" : ""}`,
        grades
      );
    }
    return {
      kind: "ready",
      text: "Here's what I understood. Open it to go through them. Nothing is approved until you press Add user on each one.",
      summary: [
        { label: "Waiting", value: joinRequests(found.length) },
        ...(grade ? [{ label: "From", value: grade }] : []),
      ],
      to: toUsers(grade ? { search: grade } : {}, "requests"),
    };
  },
};

/** "Remove Jane Dube": her page, with Remove asked. Nothing is removed until Remove User. */
const removeUserEntry: HandoffEntry = {
  id: "users.remove",
  sidebarItem: "users",
  followUpSlot: "user",
  examples: [
    "Remove someone",
    "Remove a user",
    "Remove a parent",
    "Delete a user",
    "Delete a parent",
    "Take someone off the app",
    "Take a parent off Noticeboard",
    "Remove a parent from the app",
    "Kick someone off the app",
    "Get rid of a user",
    "Remove a family that has left the school",
    ...askedHow("remove a parent who left the school"),
  ],
  handoff: async (said, ctx) => {
    const all = await loadUsers(ctx);
    if (all.length === 0) return { kind: "cannot", text: "There are no Users to remove." };
    const pick = pickUser(all, said.user ?? "", "Who would you like to remove?");
    if (isSlotQuestion(pick)) return pick;
    return {
      kind: "ready",
      text: "Here's what I understood. Open it to check. Nothing is removed until you press Remove User.",
      summary: [{ label: "Remove", value: pick.user.name }],
      to: toUserHome(pick.user.id, { remove: true }),
    };
  },
};

export const usersEntries = [
  openPageEntry("users", "It's where you manage users and invitations for your Organization.", [
    "View users",
    "Where do I invite parents?",
    "Where can I see our parents?",
    "Where are join requests?",
  ]),

  // The Users page's total, as its stats bar and Active Users tab show it, and who they are
  defineEntry<{ count: number; names: string[] }>({
    id: "users.total",
    sidebarItem: "users",
    examples: [
      "How many parents are in the app?",
      "How many users are on the Users page?",
      "How many parents do we have?",
      "Total users",
      "User count",
      "User count please",
      "How many people are on the app?",
      "Number of users",
      "How many families use the app?",
      "Count our users",
      "How many active users are there?",
      "How big is our user list?",
      "How many parents have joined?",
      "Can you email the whole parent list to me?",
      "Who are our users?",
      "List the users",
      "Who is on the app?",
    ],
    answer: {
      load: async (_, ctx) => {
        const all = await loadUsers(ctx);
        return { count: all.length, names: all.map((u) => u.name).filter(Boolean) };
      },
      templates: [
        ({ count, names }) => `You have ${users(count)} in the app${namedAfter(names)}.`,
        ({ count, names }) => `There ${isAre(count)} ${users(count)} on your Users page${namedAfter(names)}.`,
        ({ count, names }) =>
          `${capitalize(users(count))} ${count === 1 ? "has" : "have"} joined your Organization${namedAfter(names)}.`,
      ],
      link: () => openUsers(),
    },
  }),

  // One User, by name, as Users search finds them
  defineEntry<{ name: string; id: string; contact: string }>({
    id: "users.find",
    sidebarItem: "users",
    examples: [
      "Find a user",
      "Look up a parent",
      "Find a parent who is already registered",
      "Find user",
      "Is this person a user?",
      "Is the parent on the app?",
      "Look up a user",
      "Search for a parent",
      "Find a parent",
      "Is someone a user?",
      "Does this parent have an account?",
      "Is the parent a user?",
      "Look for a user by name",
      "Is this family on Noticeboard?",
      "Is Jane Dube a user?",
      "Find Jane Dube",
    ],
    slots: { user: "" },
    answer: {
      load: async ({ user: said = "" }, ctx) => {
        const all = await loadUsers(ctx);
        const name = tidyName(said.replace(FIND_WORDS, " "));
        const pick = pickUser(all, name, "Which User would you like to find?");
        if (isSlotQuestion(pick)) return pick;
        const { name: found, id, email, phoneNumber } = pick.user;
        return { name: found, id, contact: email || phoneNumber || "" };
      },
      templates: [
        ({ name, contact }) => `${name} is a User${contact ? `: ${contact}` : ""}.`,
        ({ name, contact }) => `Yes, ${name} is a User${contact ? `, at ${contact}` : ""}.`,
        ({ name, contact }) => `${name} is on your Users page${contact ? `, with ${contact}` : ""}.`,
      ],
      link: (_, { name, id }) => ({ label: `Open ${name}`, href: userHomeHref(id) }),
    },
  }),

  // The Pending Invitations tab: Invited, but not joined yet
  defineEntry<{ count: number; contacts: string[] }>({
    id: "users.unjoined",
    sidebarItem: "users",
    examples: [
      "How many parents haven't joined yet?",
      "Who hasn't opened the app yet?",
      "Who hasn't joined the app yet?",
      ...askedHow("see who has not joined yet"),
      "Who hasn't joined yet?",
      "Which parents haven't joined?",
      "Pending invitations",
      "How many invites are still pending?",
      "Who hasn't accepted their invite?",
      "Which invitations are waiting?",
      "Users who haven't signed up",
      "Who did we invite that hasn't joined?",
      "How many invitations haven't been accepted?",
      "Outstanding invites",
    ],
    answer: {
      load: async (_, ctx) => {
        const invitations = await ctx.queryClient.fetchQuery(viewQuery(ctx.user, invitationsQuery(orgOf(ctx), ctx.token)));
        return {
          // Counted as the tab counts them; listed by whatever each was sent to
          count: invitations.length,
          contacts: invitations
            .map((i) => i.email || i.phoneNumber || i.memberAlias || "")
            .filter(Boolean),
        };
      },
      templates: [
        ({ count, contacts }) =>
          count === 0
            ? "Everyone you've invited has joined: there are no Invites waiting."
            : `There ${isAre(count)} ${invites(count)} waiting to be accepted${whoTo(contacts)}.`,
        ({ count, contacts }) =>
          count === 0
            ? "There are no Invites waiting. Everyone has joined."
            : `${capitalize(invites(count))} ${isAre(count)} still pending${whoTo(contacts)}.`,
        ({ count, contacts }) =>
          count === 0
            ? "No one is still to join: you have no pending Invites."
            : `You have ${invites(count)} that haven't been accepted yet${whoTo(contacts)}.`,
      ],
      link: () => ({ label: "See Pending Invitations", href: usersHref("invitations") }),
    },
  }),

  // The Requests tab, by name
  defineEntry<{ list: string[] }>({
    id: "users.joinRequests",
    sidebarItem: "users",
    examples: [
      "Who has asked to join?",
      "Who is waiting to be let in?",
      "Who wants to join?",
      "List the join requests",
      "Show me the join requests",
      "Which families asked to join?",
      "Who sent a join request?",
      "Whose join requests are waiting?",
      "Which parents are waiting to join?",
      "Names of the join requests",
      "There are no join requests. Is that an error?",
    ],
    answer: {
      load: async (_, ctx) => ({ list: (await loadJoinRequests(ctx)).map(requestedBy) }),
      templates: [
        ({ list }) =>
          list.length === 0
            ? "No one is waiting to join right now."
            : `There ${isAre(list.length)} ${joinRequests(list.length)} waiting: ${listUpTo(list)}.`,
        ({ list }) =>
          list.length === 0
            ? "There are no Join requests waiting."
            : `${capitalize(joinRequests(list.length))} from ${listUpTo(list)}.`,
        ({ list }) =>
          list.length === 0
            ? "You're all caught up: no Join requests."
            : `You have ${joinRequests(list.length)} to look at: ${listUpTo(list)}.`,
      ],
      link: () => ({ label: "See Join requests", href: usersHref("requests") }),
    },
  }),

  // A child's family: their guardians, as Users search finds them by the child's name
  defineEntry<ChildGuardians>({
    id: "users.guardians",
    sidebarItem: "users",
    featureFlag: "familyManagement",
    examples: [
      "Who are this child's guardians?",
      "Who are Thandi Mokoena's guardians?",
      "Who are the guardians?",
      "Who are the child's parents?",
      "Who are the guardians of a child?",
      "Which parents are linked to the child?",
      "Who is in the child's family?",
      "Find a child's family",
      "Who is the learner's parent?",
      "Show me the child's guardians",
      "Which users belong to the child?",
      "Who looks after the child?",
    ],
    slots: { child: "" },
    answer: {
      load: async ({ child: said = "" }, ctx) => {
        const [children, all] = await Promise.all([
          ctx.queryClient.fetchQuery(viewQuery(ctx.user, familySearchIndexQuery(orgOf(ctx), ctx.token))),
          loadUsers(ctx),
        ]);
        const pick = pickChild(children, said);
        if (isSlotQuestion(pick)) return pick;
        const byId = new Map(all.map((u) => [u.id, u.name]));
        return {
          child: familyChildName(pick.child),
          guardians: pick.child.guardianUserIds
            .map((id) => byId.get(id))
            .filter((name): name is string => Boolean(name)),
        };
      },
      templates: [
        ({ child, guardians }) =>
          guardians.length === 0
            ? `${child} has no guardians linked yet.`
            : `${child}'s guardians are ${listOf(guardians)}.`,
        ({ child, guardians }) =>
          guardians.length === 0
            ? `No Users are linked to ${child} yet.`
            : `${listOf(guardians)} ${isAre(guardians.length)} linked to ${child}.`,
        ({ child, guardians }) =>
          guardians.length === 0
            ? `${child}'s family has no guardians on the app yet.`
            : `In ${child}'s family: ${listOf(guardians)}.`,
      ],
      link: (_, { child }) => openUsers(child),
    },
  }),

  approveJoinRequestsEntry,
  inviteEntry,
  removeUserEntry,
];
